
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Service } from "@/types/marketplace";
import { SearchFilters } from "@/components/marketplace/SearchFilters";
import { MarketplaceGrid } from "./MarketplaceGrid";
import { marketplaceData } from "./marketplaceData";
import { MarketplaceProducts } from "./MarketplaceProducts";
import { Card } from "@/components/ui/card";
import { ShoppingBag, Users, Star, DollarSign } from "lucide-react";

interface MarketplaceContainerProps {
  selectedView: "services" | "products";
}

const MarketplaceContainer = ({ selectedView }: MarketplaceContainerProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [sortBy, setSortBy] = useState("rating");
  const [priceRange, setPriceRange] = useState("all");
  const [onlineStatus, setOnlineStatus] = useState("any");
  const [expertise, setExpertise] = useState("any");
  const [language, setLanguage] = useState("any");
  const { toast } = useToast();

  const handleHire = (serviceId: number) => {
    const service = marketplaceData.find(s => s.id === serviceId);
    toast({
      title: "Request Sent!",
      description: `Your request for ${service?.title} has been sent to ${service?.seller}.`,
    });
  };

  const handleContact = (serviceId: number) => {
    toast({
      title: "Message Sent",
      description: "Your message has been sent to the service provider.",
    });
  };

  const filteredServices = marketplaceData.filter(service => {
    const matchesSearch = service.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         service.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         service.skills.some(skill => skill.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         service.seller.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (service.languages?.some(lang => lang.toLowerCase().includes(searchTerm.toLowerCase())));
    
    const matchesCategory = selectedCategory === "all" || service.skills.includes(selectedCategory);
    
    const priceNum = parseInt(service.price.split("$")[1]);
    const matchesPrice = priceRange === "all" ||
      (priceRange === "0-50" && priceNum <= 50) ||
      (priceRange === "51-100" && priceNum > 50 && priceNum <= 100) ||
      (priceRange === "101+" && priceNum > 100);
    
    const matchesOnlineStatus = onlineStatus === "any" || 
      (service.onlineStatus && onlineStatus === service.onlineStatus);
    
    const matchesExpertise = expertise === "any" || 
      (service.expertise && service.expertise.toLowerCase().includes(expertise.toLowerCase()));
    
    const matchesLanguage = language === "any" || 
      (service.languages && service.languages.some(lang => 
        lang.toLowerCase().includes(language.toLowerCase())));
    
    return matchesSearch && matchesCategory && matchesPrice && 
           matchesOnlineStatus && matchesExpertise && matchesLanguage;
  });

  const sortedServices = [...filteredServices].sort((a, b) => {
    switch (sortBy) {
      case "rating":
        return b.rating - a.rating;
      case "price":
        return parseInt(b.price.split("$")[1]) - parseInt(a.price.split("$")[1]);
      case "reviews":
        return b.reviews - a.reviews;
      default:
        return 0;
    }
  });

  return (
    <div className="animate-fade-in">
      <div className="mb-8">
        <SearchFilters
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          sortBy={sortBy}
          setSortBy={setSortBy}
          priceRange={priceRange}
          setPriceRange={setPriceRange}
          resultCount={sortedServices.length}
          onlineStatus={onlineStatus}
          setOnlineStatus={setOnlineStatus}
          expertise={expertise}
          setExpertise={setExpertise}
          language={language}
          setLanguage={setLanguage}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3">
          {selectedView === "services" ? (
            <MarketplaceGrid 
              services={sortedServices}
              onHire={handleHire}
              onContact={handleContact}
            />
          ) : (
            <MarketplaceProducts />
          )}
        </div>
        
        <div className="space-y-6">
          <Card className="p-6 bg-gradient-to-r from-sky-50 to-emerald-50">
            <h3 className="text-lg font-semibold mb-4">Marketplace Stats</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-sky-100 rounded-full">
                  <ShoppingBag className="w-5 h-5 text-sky-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Available Services</p>
                  <p className="font-semibold">{marketplaceData.length}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-full">
                  <Users className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Active Sellers</p>
                  <p className="font-semibold">42</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-full">
                  <Star className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Avg. Rating</p>
                  <p className="font-semibold">4.8 / 5</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-full">
                  <DollarSign className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Completed Orders</p>
                  <p className="font-semibold">1,248</p>
                </div>
              </div>
            </div>
          </Card>
          
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Top Categories</h3>
            <div className="space-y-3">
              {["Web Development", "UI/UX Design", "Mobile Apps", "Smart Contracts", "APIs"].map((category, index) => (
                <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span>{category}</span>
                  <span className="text-sm text-gray-500">{Math.floor(Math.random() * 30) + 10} services</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MarketplaceContainer;
